import React, { useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { getIncomeStatement } from "../../api/accountApi";
import "../Attendance/Attendance.css";

const today = () => new Date().toISOString().slice(0, 10);
const firstOfMonth = () => today().slice(0, 8) + "01";

/**
 * Direct port of digicampusbd's `general-accounts-report-income-statement`
 * ("Financial Income Statement") — Income-category ledgers vs. Expense-
 * category ledgers for a date range, each ledger net-off to whichever side
 * (debit/credit) it leans, with a Profit/Loss balancing figure. Confirmed via
 * `GeneralAccountReportService.incomeStatement`'s own two SUM/GROUP BY native
 * queries (by categoryType) + its in-Java net-off logic, ported verbatim.
 */
export default function IncomeStatementPage() {
  const [fromDate, setFromDate] = useState(firstOfMonth());
  const [toDate, setToDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSearch = async () => {
    setError("");
    setLoading(true);
    try {
      setData(await getIncomeStatement(fromDate, toDate));
    } catch (err) {
      setError("Failed to load income statement.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Financial Income Statement</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      <div className="attendance-filters">
        <label>
          From Date
          <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        </label>
        <label>
          To Date
          <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Search
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        data && (
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div className="table-scroll" style={{ flex: 1, minWidth: 320 }}>
              <h3>Income</h3>
              <table className="attendance-table">
                <thead>
                  <tr>
                    <th>Ledger</th>
                    <th>Debit</th>
                    <th>Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {data.incomeList.map((r) => (
                    <tr key={r.ledgerId}>
                      <td>{r.ledgerName}</td>
                      <td>{r.debitAmount.toFixed(2)}</td>
                      <td>{r.creditAmount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ fontWeight: 800 }}>Total Income</td>
                    <td colSpan={2}>
                      <strong>{data.totalIncome.toFixed(2)}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="table-scroll" style={{ flex: 1, minWidth: 320 }}>
              <h3>Expense</h3>
              <table className="attendance-table">
                <thead>
                  <tr>
                    <th>Ledger</th>
                    <th>Debit</th>
                    <th>Credit</th>
                  </tr>
                </thead>
                <tbody>
                  {data.expenseList.map((r) => (
                    <tr key={r.ledgerId}>
                      <td>{r.ledgerName}</td>
                      <td>{r.debitAmount.toFixed(2)}</td>
                      <td>{r.creditAmount.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td style={{ fontWeight: 800 }}>Total Expense</td>
                    <td colSpan={2}>
                      <strong>{data.totalExpense.toFixed(2)}</strong>
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div style={{ flexBasis: "100%", fontWeight: 800, fontSize: "1.1em" }}>
              {data.profitOrLoss}: {data.profitOrLossAmount.toFixed(2)}
            </div>
          </div>
        )
      )}
    </MainLayout>
  );
}
